let turnstileScriptPromise: Promise<void> | null = null;

export const TURNSTILE_SITE_KEY_PLACEHOLDER = "TURNSTILE_SITE_KEY";
export const DEFAULT_TURNSTILE_SITE_KEY = "0x4AAAAAAB8FDJ-87NV9rbaG";

export type TurnstileCallback = (token: string) => void;

type TurnstileRenderOptions = {
  sitekey: string;
  callback?: TurnstileCallback;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  appearance?: "always" | "execute" | "interaction-only";
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
};

type TurnstileAPI = {
  render: (container: HTMLElement | string, options: TurnstileRenderOptions) => string;
  remove: (id: string) => void;
  reset: (id?: string) => void;
  getResponse: (id?: string) => string | null;
};

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

export const loadTurnstile = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Cloudflare Turnstile script"));
      document.head.appendChild(script);
    });
  }

  return turnstileScriptPromise;
};

export const getTurnstileSiteKey = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const fromWindow = window.turnstileSiteKey?.trim();
  if (fromWindow && fromWindow !== TURNSTILE_SITE_KEY_PLACEHOLDER) {
    return fromWindow;
  }

  const fromEnv = process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim();
  if (fromEnv) {
    return fromEnv;
  }

  if (process.env.NODE_ENV === "development") {
    return DEFAULT_TURNSTILE_SITE_KEY;
  }

  return fromWindow || null;
};